/**
 * Helpers to treat employee DMs as Conversations-list rows.
 */

import type { Conversation as DmConversation } from "@/widgets/frontdesk/providers/internal/types/direct-messages.types";
import type { ConversationMember } from "@/widgets/frontdesk/providers/internal/types/direct-messages.types";
import { formatInternalChatPreview, localizeQuoteApprovalConversationTitle } from "@/widgets/quote-new/buttons-container/build-quote-approval-message";
import i18n from "i18next";

/** Sentinel inbox_id for the Direct Messages channel chip (not sent to Chatwoot API). */
export const INTERNAL_INBOX_FILTER_ID = -1;

export const INTERNAL_SOURCE = "internal";

export function isInternalInboxFilter(inboxId?: number | null): boolean {
  return inboxId === INTERNAL_INBOX_FILTER_ID;
}

/** Chatwoot IDs are numeric; DM conversation IDs are UUIDs (or other non-numeric strings). */
export function looksLikeInternalConversationId(
  id?: string | number | null,
): boolean {
  if (id == null || id === "") return false;
  return !/^\d+$/.test(String(id));
}

export function isInternalConversation(conversation: {
  custom_attributes?: Record<string, unknown> | null;
  inbox_id?: number | null;
}): boolean {
  if (conversation?.custom_attributes?.gomake_source === INTERNAL_SOURCE) {
    return true;
  }
  return conversation?.inbox_id === INTERNAL_INBOX_FILTER_ID;
}

export function isInternalGroupConversation(conversation: {
  custom_attributes?: Record<string, unknown> | null;
}): boolean {
  if (!isInternalConversation(conversation)) return false;
  const attrs = conversation.custom_attributes || {};
  if (attrs.is_group === true) return true;
  return attrs.internal_type === "GROUP_DM";
}

export function getInternalConversationId(conversation: {
  id?: string | number;
  custom_attributes?: Record<string, unknown> | null;
}): string | null {
  if (!isInternalConversation(conversation)) return null;
  const fromAttr = conversation.custom_attributes?.internal_conversation_id;
  if (typeof fromAttr === "string" && fromAttr) return fromAttr;
  return conversation.id != null ? String(conversation.id) : null;
}

/** Full display name for a member (prefer real name / email over "Employee"). */
export function memberDisplayName(m: ConversationMember): string {
  const fromParts = `${m.firstName || ""} ${m.lastName || ""}`.trim();
  if (fromParts) return fromParts;
  const rawName = String(m.name || "").trim();
  if (rawName && rawName.toLowerCase() !== "employee") return rawName;
  const email = String(m.email || "").trim();
  if (email) return email.split("@")[0] || email;
  return "Employee";
}

/** Short initials for list/header (e.g. AM, QA). */
export function memberInitials(m: ConversationMember): string {
  const first = String(m.firstName || "").trim();
  const last = String(m.lastName || "").trim();
  if (first || last) {
    return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase() || "?";
  }

  const rawName = String(m.name || "").trim();
  if (rawName && rawName.toLowerCase() !== "employee") {
    const parts = rawName.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
    }
    const dotted = rawName.split(/[.\s_-]+/).filter(Boolean);
    if (dotted.length >= 2) {
      return `${dotted[0].charAt(0)}${dotted[1].charAt(0)}`.toUpperCase();
    }
    return rawName.slice(0, 2).toUpperCase();
  }

  const emailLocal = String(m.email || "")
    .split("@")[0]
    .trim();
  const emailParts = emailLocal.split(/[.\s_-]+/).filter(Boolean);
  if (emailParts.length >= 2) {
    return `${emailParts[0].charAt(0)}${emailParts[1].charAt(0)}`.toUpperCase();
  }
  return (emailLocal.slice(0, 2) || "?").toUpperCase();
}

export function getInternalMemberNamesTooltip(conversation: {
  custom_attributes?: Record<string, unknown> | null;
  meta?: { sender?: { name?: string } } | null;
}): string {
  const full = conversation.custom_attributes?.member_names_full;
  if (typeof full === "string" && full.trim()) return full.trim();
  return String(conversation.meta?.sender?.name || "").trim();
}

function isCurrentMember(
  m: ConversationMember,
  currentUserIds: Array<string | null | undefined>,
  currentEmail?: string | null,
): boolean {
  const idSet = new Set(
    currentUserIds.filter(Boolean).map((id) => String(id).toLowerCase()),
  );
  if (m.userId && idSet.has(String(m.userId).toLowerCase())) return true;
  if (
    currentEmail &&
    m.email &&
    m.email.toLowerCase() === currentEmail.toLowerCase()
  ) {
    return true;
  }
  return false;
}

export interface MapDmOptions {
  currentUserId?: string | null;
  /** Extra id candidates (profile.userId, employeeId, etc.) */
  currentUserIds?: Array<string | null | undefined>;
  currentUserEmail?: string | null;
  currentUserName?: string | null;
  currentUserFirstName?: string | null;
  currentUserLastName?: string | null;
}

/**
 * Map a DM conversation into a Chatwoot-like list row for ticket cards.
 */
export function mapDmToListConversation(
  dm: DmConversation,
  currentUserIdOrOptions?: string | null | MapDmOptions,
): Record<string, unknown> {
  const options: MapDmOptions =
    typeof currentUserIdOrOptions === "object" && currentUserIdOrOptions !== null
      ? currentUserIdOrOptions
      : { currentUserId: currentUserIdOrOptions as string | null | undefined };

  const idCandidates = [
    options.currentUserId,
    ...(options.currentUserIds || []),
  ];

  const members = dm.members || [];
  const others = members.filter(
    (m) => !isCurrentMember(m, idCandidates, options.currentUserEmail),
  );

  const isGroup =
    dm.type === "GROUP_DM" ||
    (typeof dm.participantCount === "number"
      ? dm.participantCount > 2
      : others.length > 1);

  const fullNames = others.map(memberDisplayName).filter(Boolean);
  const initialsLabel = others.map(memberInitials).filter(Boolean).join(", ");
  const fullNamesLabel = fullNames.join(", ");

  // 1:1 → full name. Group → full names of participants.
  let displayName: string;
  let primary: ConversationMember | undefined;

  if (isGroup) {
    primary = others[0];
    displayName =
      localizeQuoteApprovalConversationTitle(
        dm.name,
        i18n.t.bind(i18n),
        dm.contextType,
      ) ||
      dm.name?.trim() ||
      fullNamesLabel ||
      "Group chat";
  } else {
    primary = others[0];
    displayName =
      localizeQuoteApprovalConversationTitle(
        dm.name,
        i18n.t.bind(i18n),
        dm.contextType,
      ) ||
      dm.name?.trim() ||
      (primary ? memberDisplayName(primary) : "Direct Message");
  }

  let unixSeconds = Math.floor(Date.now() / 1000);
  if (dm.lastMessageAt) {
    const ms = new Date(dm.lastMessageAt).getTime();
    if (!Number.isNaN(ms)) {
      unixSeconds = Math.floor(ms / 1000);
    }
  }

  const lastContent = formatInternalChatPreview(
    dm.lastMessage?.content || "",
    i18n.t.bind(i18n),
  );
  const lastMessagePreview =
    lastContent.length > 100
      ? `${lastContent.substring(0, 100)}...`
      : lastContent;

  const assigneeId =
    options.currentUserId ||
    options.currentUserIds?.find(Boolean) ||
    null;
  const assigneeName =
    String(options.currentUserName || "").trim() ||
    `${options.currentUserFirstName || ""} ${options.currentUserLastName || ""}`.trim() ||
    String(options.currentUserEmail || "").trim() ||
    "Me";

  return {
    id: dm.id,
    inbox_id: INTERNAL_INBOX_FILTER_ID,
    status: "open",
    unread_count: dm.unreadCount || 0,
    created_at: unixSeconds,
    updated_at: unixSeconds,
    last_activity_at: unixSeconds,
    timestamp: unixSeconds,
    // Current user is a participant; list filter decides My Tickets vs All
    assignee_id: assigneeId,
    meta: {
      sender: {
        id: primary?.userId,
        name: displayName,
        email: primary?.email || "",
        type: "contact",
        ...(isGroup ? { thumbnail: null, avatar_url: null } : {}),
      },
      assignee: assigneeId
        ? {
            id: assigneeId,
            name: assigneeName,
            available_name: assigneeName,
            email: options.currentUserEmail || "",
          }
        : undefined,
      channel: "Channel::Internal",
    },
    additional_attributes: {
      subject: lastMessagePreview,
      mail_subject: lastMessagePreview,
    },
    custom_attributes: {
      gomake_source: INTERNAL_SOURCE,
      internal_conversation_id: dm.id,
      internal_type: dm.type || (isGroup ? "GROUP_DM" : "DM"),
      is_group: isGroup,
      member_initials: isGroup ? initialsLabel : undefined,
      member_names_full: fullNamesLabel || undefined,
      related_to_me: true,
      context_type: dm.contextType || undefined,
      context_id: dm.contextId || undefined,
    },
    labels: [],
    messages: [],
    last_non_activity_message: null,
  };
}
